/**
 * SocialPulseHelp — /help (Help Centre).
 *
 * SocialPulse-branded help hub: quick answers, popular topics, contact
 * channels and a lightweight FAQ. Uses the SocialPulse palette
 * (emerald / violet / blue / orange / lavender) with no dark surfaces.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Search, ArrowRight, LifeBuoy, MessageCircle, Mail, Shield,
  UserPlus, Compass, Users, Landmark, Settings, HelpCircle, ChevronDown,
} from "lucide-react";
import SocialPulseHeader from "@/components/socialpulse/SocialPulseHeader";
import SocialPulseFooter from "@/components/socialpulse/SocialPulseFooter";

const QUICK_LINKS = [
  { key: "start",     label: "Getting started",  icon: UserPlus,  tint: "emerald", href: "#faq-getting-started" },
  { key: "explore",   label: "Exploring places", icon: Compass,   tint: "blue",    href: "#faq-explore" },
  { key: "community", label: "Community & posts",icon: Users,     tint: "violet",  href: "#faq-community" },
  { key: "civic",     label: "Civic reporting",  icon: Landmark,  tint: "teal",    href: "#faq-civic" },
  { key: "account",   label: "Account & privacy",icon: Shield,    tint: "rose",    href: "#faq-account" },
  { key: "settings",  label: "Settings & data",  icon: Settings,  tint: "orange",  href: "#faq-settings" },
];

const FAQS = [
  {
    id: "faq-getting-started",
    section: "Getting started",
    items: [
      { q: "What is SocialPulse?",
        a: "SocialPulse® is a network of geographic and community platforms. One account works across every sister site — CivicPulse.blog, Indias.com, Kerala.com, WorldPulse.pro, and more." },
      { q: "Do I need to sign up separately for each sister site?",
        a: "No. Sign in once on any SocialPulse network site and you're recognised everywhere. Your profile, saved places and civic reports travel with you." },
      { q: "Is SocialPulse free to use?",
        a: "Reading, exploring and most participation are free. Some specialist services (verified organization pages, premium listings) may involve a paid tier as they roll out." },
    ],
  },
  {
    id: "faq-explore",
    section: "Exploring places",
    items: [
      { q: "How do I find my town or village?",
        a: "Type its name into the search bar at the top. If a portal exists we'll take you straight there. If it doesn't yet, you can request one from the /places page." },
      { q: "Can I contribute information about my community?",
        a: "Yes. Signed-in members can add photos, stories, corrections and events. Reference facts (population, geography) come from public sources and community edits go through moderation." },
    ],
  },
  {
    id: "faq-community",
    section: "Community & posts",
    items: [
      { q: "What can I post?",
        a: "Community discussions, event notices, local photos, volunteer requests, alumni stories, and civic reports. Please keep it constructive and follow our community guidelines." },
      { q: "How is moderation handled?",
        a: "A mix of community reporting, automated safety checks and human moderators. You can report any post via the ‘…’ menu on any card." },
    ],
  },
  {
    id: "faq-civic",
    section: "Civic reporting",
    items: [
      { q: "How do I report a civic issue?",
        a: "Head to CivicPulse.blog or the /civic tab, tap ‘Report’, add a photo, describe the problem, and pick a category. We'll auto-route it to the correct authority." },
      { q: "Are faces and licence plates redacted?",
        a: "Yes. Photos submitted to CivicPulse are run through a privacy filter that blurs faces and vehicle plates before publication." },
    ],
  },
  {
    id: "faq-account",
    section: "Account & privacy",
    items: [
      { q: "How do I delete my account?",
        a: "Go to Settings → Account → Delete account. Your public posts remain visible in anonymised form (attributed to ‘A former member’) unless you request full removal via legal@socialpulse.com." },
      { q: "Who can see my profile?",
        a: "By default only your display name is public. You can opt in to a public profile at Settings → Privacy." },
    ],
  },
  {
    id: "faq-settings",
    section: "Settings & data",
    items: [
      { q: "How do I change the language?",
        a: "Tap the language name (e.g. ‘English’) in the top-right corner. SocialPulse supports 13 languages including Malayalam, Tamil, Telugu, Hindi, Bengali, French, Spanish and Arabic." },
      { q: "Can I export my data?",
        a: "Yes. Settings → Data → Request export. We'll email you a ZIP within 48 hours." },
    ],
  },
];

const CONTACTS = [
  { icon: MessageCircle, label: "Community forum",  desc: "Ask the SocialPulse community",  href: "/community", tint: "violet" },
  { icon: Mail,          label: "Email support",    desc: "support@socialpulse.com",        href: "mailto:support@socialpulse.com", tint: "blue" },
  { icon: Shield,        label: "Report abuse",     desc: "For urgent safety concerns",     href: "mailto:trust@socialpulse.com", tint: "rose" },
];

export default function SocialPulseHelp() {
  const [q, setQ] = useState("");

  useEffect(() => {
    document.title = "Help Centre · SocialPulse";
    setMeta("description", "The SocialPulse Help Centre — quick answers, FAQs, and ways to reach a human across the SocialPulse network.");
    setLink("canonical", "https://socialpulse.com/help");
    setMeta("robots", "index, follow");
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return FAQS;
    return FAQS
      .map((s) => ({
        ...s,
        items: s.items.filter(({ q: qq, a }) => qq.toLowerCase().includes(needle) || a.toLowerCase().includes(needle)),
      }))
      .filter((s) => s.items.length > 0);
  }, [q]);

  return (
    <div className="min-h-screen bg-white" data-testid="sp-help-page">
      <SocialPulseHeader />

      {/* Hero */}
      <section
        className="relative overflow-hidden"
        style={{
          background:
            "linear-gradient(140deg, #F0F6FF 0%, #F5F1FA 40%, #ECFDF5 100%)",
        }}
      >
        <div className="max-w-[1200px] mx-auto px-5 sm:px-6 lg:px-10 pt-12 sm:pt-16 pb-8 sm:pb-10">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[.22em] text-violet-700">
            <LifeBuoy className="w-3.5 h-3.5" /> Help Centre
          </div>
          <h1
            className="mt-3 font-serif font-bold tracking-tight text-slate-900 leading-[1.05] text-3xl sm:text-4xl lg:text-5xl"
            data-testid="sp-help-h1"
          >
            How can we <span className="text-emerald-700">help?</span>
          </h1>
          <p className="mt-4 text-slate-600 text-base sm:text-lg leading-relaxed max-w-xl">
            Search for answers, browse popular topics, or reach out — we're
            here to make SocialPulse easy for everyone.
          </p>

          {/* Search */}
          <div className="mt-6 sm:mt-8 max-w-xl">
            <label className="relative block">
              <Search className="w-4 h-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search help topics…"
                data-testid="sp-help-search"
                className="w-full h-12 pl-11 pr-4 rounded-full ring-1 ring-slate-200 focus:ring-2 focus:ring-violet-500 focus:outline-none text-sm text-slate-800 placeholder:text-slate-400 bg-white shadow-sm"
              />
            </label>
          </div>
        </div>
      </section>

      {/* Quick links */}
      <section className="max-w-[1200px] mx-auto px-5 sm:px-6 lg:px-10 py-10 sm:py-12">
        <h2 className="font-serif font-bold text-slate-900 text-xl sm:text-2xl">
          Browse popular topics
        </h2>
        <ul
          className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3"
          data-testid="sp-help-quicklinks"
        >
          {QUICK_LINKS.map((l) => <QuickLink key={l.key} {...l} />)}
        </ul>
      </section>

      {/* FAQs */}
      <section className="max-w-[1200px] mx-auto px-5 sm:px-6 lg:px-10 pb-14">
        <h2 className="font-serif font-bold text-slate-900 text-xl sm:text-2xl">
          Frequently asked questions
        </h2>
        {filtered.length === 0 && (
          <p className="mt-6 text-sm text-slate-500" data-testid="sp-help-empty">
            No FAQs match “{q}”. Try a different word, or <a href="mailto:support@socialpulse.com" className="text-violet-700 hover:text-violet-900 font-semibold">email us</a>.
          </p>
        )}
        <div className="mt-6 space-y-8">
          {filtered.map((s) => (
            <div key={s.id} id={s.id} data-testid={`sp-help-section-${s.id}`}>
              <h3 className="font-serif font-bold text-emerald-700 text-lg mb-3">
                {s.section}
              </h3>
              <ul className="rounded-2xl bg-white ring-1 ring-slate-200 divide-y divide-slate-100 overflow-hidden">
                {s.items.map((it, i) => <FaqRow key={i} q={it.q} a={it.a} />)}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* Contact */}
      <section className="max-w-[1200px] mx-auto px-5 sm:px-6 lg:px-10 pb-16">
        <div
          className="rounded-3xl ring-1 ring-violet-200 p-6 sm:p-8"
          style={{
            background:
              "linear-gradient(135deg, #F5F1FA 0%, #EEF2FF 45%, #ECFDF5 100%)",
          }}
          data-testid="sp-help-contact"
        >
          <h2 className="font-serif font-bold text-slate-900 text-xl sm:text-2xl">
            Still need help?
          </h2>
          <p className="mt-1 text-slate-600 text-sm sm:text-base">
            Choose the fastest way to reach a real human.
          </p>
          <ul className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
            {CONTACTS.map((c) => <ContactCard key={c.label} {...c} />)}
          </ul>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/join"
              data-testid="sp-help-cta-join"
              className="inline-flex items-center gap-2 rounded-full bg-violet-600 hover:bg-violet-700 text-white font-semibold px-5 py-2.5 text-sm transition-colors shadow-sm"
            >
              Create an account <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/network"
              data-testid="sp-help-cta-network"
              className="inline-flex items-center gap-2 rounded-full ring-1 ring-slate-300 hover:ring-violet-400 text-slate-700 hover:text-violet-700 font-semibold px-5 py-2.5 text-sm transition-colors"
            >
              Explore the network
            </Link>
          </div>
        </div>
      </section>

      <SocialPulseFooter />
    </div>
  );
}

/* ─────────────────── UI atoms ─────────────────── */

const TINTS = {
  violet:   { bg: "bg-violet-50",   text: "text-violet-700",   ring: "ring-violet-200" },
  emerald:  { bg: "bg-emerald-50",  text: "text-emerald-700",  ring: "ring-emerald-200" },
  blue:     { bg: "bg-blue-50",     text: "text-blue-700",     ring: "ring-blue-200" },
  orange:   { bg: "bg-orange-50",   text: "text-orange-700",   ring: "ring-orange-200" },
  rose:     { bg: "bg-rose-50",     text: "text-rose-700",     ring: "ring-rose-200" },
  teal:     { bg: "bg-teal-50",     text: "text-teal-700",     ring: "ring-teal-200" },
};

function QuickLink({ label, icon: Icon, tint, href }) {
  const t = TINTS[tint] || TINTS.violet;
  return (
    <li>
      <a
        href={href}
        data-testid={`sp-help-ql-${label.toLowerCase().replace(/\s+/g, "-")}`}
        className="flex flex-col items-center text-center gap-2 rounded-2xl bg-white ring-1 ring-slate-200 p-4 hover:ring-violet-300 hover:shadow-md transition-all h-full"
      >
        <span className={`w-10 h-10 grid place-items-center rounded-xl ${t.bg} ${t.text} ring-1 ${t.ring}`}>
          <Icon className="w-4 h-4" strokeWidth={2.2} />
        </span>
        <span className="text-[13px] font-semibold text-slate-800 leading-tight">
          {label}
        </span>
      </a>
    </li>
  );
}

function FaqRow({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <li>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid={`sp-help-faq-${q.slice(0, 20).replace(/\W+/g, "-").toLowerCase()}`}
        className="w-full flex items-center gap-3 text-left px-5 py-4 hover:bg-slate-50 transition-colors"
      >
        <HelpCircle className="w-4 h-4 text-violet-500 shrink-0" />
        <span className="flex-1 min-w-0 font-semibold text-slate-800 text-[15px]">
          {q}
        </span>
        <ChevronDown
          className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div className="px-5 pb-4 pl-12 text-[14px] text-slate-600 leading-relaxed">
          {a}
        </div>
      )}
    </li>
  );
}

function ContactCard({ icon: Icon, label, desc, href, tint }) {
  const t = TINTS[tint] || TINTS.violet;
  return (
    <li>
      <a
        href={href}
        data-testid={`sp-help-contact-${label.toLowerCase().replace(/\s+/g, "-")}`}
        className="group flex items-start gap-3 rounded-2xl bg-white ring-1 ring-slate-200 p-4 hover:ring-violet-300 hover:shadow-md transition-all h-full"
      >
        <span className={`w-10 h-10 grid place-items-center rounded-xl ${t.bg} ${t.text} ring-1 ${t.ring} shrink-0`}>
          <Icon className="w-4 h-4" strokeWidth={2.2} />
        </span>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-slate-900 text-sm">{label}</p>
          <p className="mt-0.5 text-xs text-slate-500">{desc}</p>
        </div>
        <ArrowRight className="w-4 h-4 text-slate-400 shrink-0 group-hover:translate-x-1 group-hover:text-violet-600 transition-all" />
      </a>
    </li>
  );
}

function setMeta(name, content, attr = "name") {
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
  el.setAttribute("content", content);
}
function setLink(rel, href) {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); document.head.appendChild(el); }
  el.setAttribute("href", href);
}
